/**
 * SCORM template loader + renderers.
 *
 * Loads the static SCORM template tree at build time via Vite `?raw` and
 * exposes pure functions that fill the placeholders with per-export values.
 *
 * Spec: scorm_package_spec.md §4 (template tree) and §5 (configuration.js).
 */

import type {
  ScormPackageConfig,
  ScormVersion,
} from '@course-authoring/shared-types';

import metadataXml from '../../scorm-templates/shared/metadata.xml?raw';
import indexApiHtml from '../../scorm-templates/shared/rxd/indexAPI.html?raw';
import blankHtml from '../../scorm-templates/shared/rxd/blank.html?raw';
import goodbyeHtml from '../../scorm-templates/shared/rxd/goodbye.html?raw';
import configurationJsTemplate from '../../scorm-templates/shared/rxd/configuration.template.js?raw';
import scorm12ManifestTemplate from '../../scorm-templates/manifests/scorm12.template.xml?raw';
import scorm2004_3rdManifestTemplate from '../../scorm-templates/manifests/scorm2004_3rd.template.xml?raw';
import scorm2004_4thManifestTemplate from '../../scorm-templates/manifests/scorm2004_4th.template.xml?raw';
import adlcpRootXsd from '../../scorm-templates/schemas/scorm12/adlcp_rootv1p2.xsd?raw';
import imsXmlXsd from '../../scorm-templates/schemas/scorm12/ims_xml.xsd?raw';
import imscpRootXsd from '../../scorm-templates/schemas/scorm12/imscp_rootv1p1p2.xsd?raw';
import imsmdRootXsd from '../../scorm-templates/schemas/scorm12/imsmd_rootv1p2p1.xsd?raw';

/**
 * The launchable wrapper files the manifest's `<file>` list declares (plus the
 * manifest itself, omitted by SCORM rules). Version-specific schema files ride
 * in the zip unlisted via `ScormTemplate.schemaFiles` — strict LMS importers
 * (Sana) require them present but neither Sana-validated packages nor the
 * legacy hoot template list them here. The course package's player / data /
 * assets DO NOT belong here — they live in the separately-hosted course
 * package, reached via `RXDConfig.contentUrl`.
 */
export const SCORM_ZIP_FILE_PATHS = [
  'metadata.xml',
  'rxd/indexAPI.html',
  'rxd/blank.html',
  'rxd/goodbye.html',
  'rxd/configuration.js',
] as const;

export interface ScormStaticFiles {
  'metadata.xml': string;
  'rxd/indexAPI.html': string;
  'rxd/blank.html': string;
  'rxd/goodbye.html': string;
}

export interface ScormTemplate {
  staticFiles: ScormStaticFiles;
  /**
   * SCORM schema (XSD) files bundled at the zip root, keyed by filename.
   * Written verbatim (never templated) and deliberately absent from the
   * manifest `<file>` list. Empty for versions whose schema set we have no
   * LMS to validate against (currently both SCORM 2004 editions).
   */
  schemaFiles: Record<string, string>;
  configurationTemplate: string;
  manifestTemplate: string;
}

const MANIFEST_TEMPLATES: Record<ScormVersion, string> = {
  '1.2': scorm12ManifestTemplate,
  '2004_3rd': scorm2004_3rdManifestTemplate,
  '2004_4th': scorm2004_4thManifestTemplate,
};

/**
 * SCORM 1.2 conformance ships the controlling schema files inside the package,
 * and strict LMS importers (Sana) reject manifests whose `xsi:schemaLocation`
 * they cannot resolve locally. Copied byte-for-byte from hoot-server's
 * field-proven `packageTemplates/scorm12/` set — packageBundler tests pin
 * their hashes.
 */
const SCORM_SCHEMA_FILES: Record<ScormVersion, Record<string, string>> = {
  '1.2': {
    'adlcp_rootv1p2.xsd': adlcpRootXsd,
    'ims_xml.xsd': imsXmlXsd,
    'imscp_rootv1p1p2.xsd': imscpRootXsd,
    'imsmd_rootv1p2p1.xsd': imsmdRootXsd,
  },
  '2004_3rd': {},
  '2004_4th': {},
};

/**
 * Rustici Cross-Domain driver file per SCORM version, baked into
 * `RXDConfig.driverFile` and loaded by `indexAPI.html`. SCORM 2004 4th reuses
 * the 2004 3rd-edition driver (there is no separate 4th-edition driver). These
 * files are co-hosted in the deployed package at `scorm-rustici/rxd/`.
 */
const SCORM_DRIVER_FILES: Record<ScormVersion, string> = {
  '1.2': 'rustici-driver-scorm12.min.js',
  '2004_3rd': 'rustici-driver-scorm2004_3rd.min.js',
  '2004_4th': 'rustici-driver-scorm2004_3rd.min.js',
};

export function getScormTemplate(version: ScormVersion): ScormTemplate {
  return {
    staticFiles: {
      'metadata.xml': metadataXml,
      'rxd/indexAPI.html': indexApiHtml,
      'rxd/blank.html': blankHtml,
      'rxd/goodbye.html': goodbyeHtml,
    },
    schemaFiles: SCORM_SCHEMA_FILES[version] ?? {},
    configurationTemplate: configurationJsTemplate,
    manifestTemplate: getManifestTemplate(version),
  };
}

function getManifestTemplate(version: ScormVersion): string {
  const template = MANIFEST_TEMPLATES[version];
  if (!template) {
    throw new Error(`No SCORM manifest template registered for version ${version}`);
  }
  return template;
}

/**
 * Escapes a string so it is safe to interpolate inside a JS double-quoted
 * string literal. Handles backslash, double-quote, newlines, and the
 * `</script>` sequence per spec §9. Modern JS engines treat U+2028 / U+2029
 * as whitespace so we don't bother escaping them.
 */
export function escapeJsString(input: string): string {
  return input
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n')
    .replace(/<\/script/gi, '<\\/script');
}

export function escapeXml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&apos;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char] || char);
}

export interface RenderConfigurationInput {
  scorm: ScormPackageConfig;
  courseTitle: string;
  /** SCORM version — selects the Rustici driver file. Defaults to `1.2`. */
  version?: ScormVersion;
}

/**
 * Renders rxd/configuration.js. The contentUrl is `${coursePackageUrl}` plus
 * the `API_KEY`/`INT_KEY` query string per spec §5 — when the LMS launches
 * the SCO, contentAPI.html opens that URL in the innermost iframe, which
 * resolves to the deployed course package's `index.html`.
 */
export function renderScormConfiguration(input: RenderConfigurationInput): string {
  const { scorm, courseTitle } = input;

  const contentUrl = appendCredentialsToCourseUrl({
    coursePackageUrl: scorm.coursePackageUrl,
    apiKey: scorm.apiKey,
    integrationKey: scorm.integrationKey,
    enforceSequence: scorm.enforceSequence,
    testMode: scorm.testMode,
  });

  const rawRxdHostUrl = scorm.rxdHostUrl ?? derivePackageLocalRxdHostUrl(scorm.coursePackageUrl);
  // Rustici's `rustici-xd-pkg.min.js` reads `RXDConfig.rxdHostUrl` verbatim and
  // string-concatenates `rustici-xd-pkg-to-lms.min.js` onto it. Without a
  // trailing slash, the URL collapses (e.g. `…/rxdrustici-xd-pkg-to-lms.min.js`),
  // 404s as HTML, and Chrome ORB blocks it → "LMS conduit hasn't loaded".
  const rxdHostUrl = rawRxdHostUrl.endsWith('/') ? rawRxdHostUrl : `${rawRxdHostUrl}/`;

  const driverFile = SCORM_DRIVER_FILES[input.version ?? '1.2'];

  return configurationJsTemplate
    .replace('{{CONTENT_URL}}', escapeJsString(contentUrl))
    .replace('{{COURSE_TITLE}}', escapeJsString(courseTitle))
    .replace('{{RXD_HOST_URL}}', escapeJsString(rxdHostUrl))
    .replace('{{DRIVER_FILE}}', escapeJsString(driverFile));
}

interface AppendCredentialsInput {
  coursePackageUrl: string;
  apiKey: string;
  integrationKey: string;
  enforceSequence: boolean;
  testMode?: boolean;
}

/**
 * The deployed index.html references package files relatively (`player/...`,
 * `data/...`). Directory URLs therefore need a trailing slash; otherwise a
 * bare `.../current` document resolves those assets from the parent path.
 */
export function normalizeCoursePackageUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  try {
    const parsed = new URL(trimmed);
    const lastSegment = parsed.pathname.split('/').filter(Boolean).at(-1) ?? '';
    if (!parsed.pathname.endsWith('/') && !/\.html?$/i.test(lastSegment)) {
      parsed.pathname = `${parsed.pathname}/`;
    }
    return parsed.toString();
  } catch {
    if (/\.html?(\?|#|$)/i.test(trimmed)) return trimmed;
    const splitAt = trimmed.search(/[?#]/);
    if (splitAt === -1) return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
    const base = trimmed.slice(0, splitAt);
    const suffix = trimmed.slice(splitAt);
    return base.endsWith('/') ? trimmed : `${base}/${suffix}`;
  }
}

/**
 * Joins the user-supplied course-package URL with the SCORM credential query
 * string. Preserves any path the user already typed, and merges with any
 * existing query string they happened to include.
 */
export function appendCredentialsToCourseUrl(input: AppendCredentialsInput): string {
  const trimmed = normalizeCoursePackageUrl(input.coursePackageUrl);
  const [base, existingQuery] = trimmed.split('?', 2);
  const params = new URLSearchParams(existingQuery ?? '');
  params.set('API_KEY', input.apiKey);
  params.set('INT_KEY', input.integrationKey);
  if (input.enforceSequence) {
    params.set('enforceSequence', '1');
  }
  if (input.testMode) {
    params.set('testMode', '1');
  }
  return `${base}?${params.toString()}`;
}

/**
 * Temporary SCORM testing topology: package the Rustici bridge beside the
 * hosted player and point RXD at that package-local directory. This keeps
 * contentAPI.html same-origin with the player while AuthorAI CI hosts test
 * content. Revisit when the final SCORM deployment model is defined.
 */
export function derivePackageLocalRxdHostUrl(coursePackageUrl: string): string {
  const url = new URL(coursePackageUrl.trim());
  const path = url.pathname || '/';
  const lastSlash = path.lastIndexOf('/');
  const lastSegment = lastSlash >= 0 ? path.slice(lastSlash + 1) : path;
  const basePath = path.endsWith('/')
    ? path
    : lastSegment.includes('.')
      ? path.slice(0, lastSlash + 1)
      : `${path}/`;

  url.pathname = `${basePath}scorm-rustici/rxd/`.replace(/\/{2,}/g, '/');
  url.search = '';
  url.hash = '';
  return url.toString();
}

export interface RenderManifestInput {
  version: ScormVersion;
  courseId: string;
  courseTitle: string;
}

export interface RenderStaticFileInput {
  body: string;
  courseTitle: string;
}

/**
 * Renders static SCORM wrapper files that may contain template placeholders.
 * These files are HTML/XML, so XML entity escaping is appropriate for titles.
 */
export function renderScormStaticFile(input: RenderStaticFileInput): string {
  return input.body.replace(/\{\{COURSE_TITLE\}\}/g, escapeXml(input.courseTitle));
}

/**
 * Renders imsmanifest.xml for the requested SCORM version. The SCO's
 * `<resource href>` always points at `rxd/indexAPI.html` per spec §6.1.
 * The `<file>` list is fixed — only the files that actually live in the SCORM
 * zip (course package files are NOT listed; they live elsewhere).
 */
export function renderScormManifest(input: RenderManifestInput): string {
  const template = getManifestTemplate(input.version);
  const identifier = sanitizeIdentifier(`course-${input.courseId}`);
  const fileList = renderFileList(SCORM_ZIP_FILE_PATHS);

  return template
    .replace(/\{\{IDENTIFIER\}\}/g, identifier)
    .replace(/\{\{COURSE_TITLE\}\}/g, escapeXml(input.courseTitle))
    .replace('{{FILE_LIST}}', fileList);
}

/**
 * Renders the `<file href="..."/>` list. Skips imsmanifest.xml itself —
 * SCORM forbids the manifest from listing itself in its own resource files.
 */
function renderFileList(paths: ReadonlyArray<string>): string {
  const indent = '      ';
  return paths
    .filter((p) => p !== 'imsmanifest.xml')
    .map((p) => `${indent}<file href="${escapeXml(p)}"/>`)
    .join('\n');
}

function sanitizeIdentifier(value: string): string {
  const cleaned = value.replace(/[^A-Za-z0-9_.-]/g, '_');
  return /^[A-Za-z_]/.test(cleaned) ? cleaned : `id_${cleaned}`;
}

/**
 * Returns null when scorm config is valid, or the list of missing required
 * wrapper field names. Used by the bundler before rendering the ZIP.
 */
export function validateScormConfig(scorm: ScormPackageConfig | undefined | null): string[] | null {
  if (!scorm) {
    return ['scorm'];
  }
  const missing: string[] = [];
  if (!scorm.coursePackageUrl?.trim()) missing.push('coursePackageUrl');
  if (!scorm.apiKey?.trim()) missing.push('apiKey');
  if (!scorm.integrationKey?.trim()) missing.push('integrationKey');
  return missing.length > 0 ? missing : null;
}
